import React, { FC, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { Table, TableHead, TableRow, TableBody, TableCell } from '@mui/material';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList as List } from 'react-window';
import Error from 'components/Error/Error';
import Loading from 'components/Loading/Loading';
import PaymentReportDownload from 'components/Payment/PaymentReportDownload/PaymentReportDownload';
import { useAppSelector } from 'reduxState/hooks';
import { IPayment } from 'reduxState/store/payment/types';
import { useGetUserApplicationsQuery } from 'reduxState/store/application/api';
import { AppGroup } from 'reduxState/store/application/types';
import { useLazyGetPaymentHistoryQuery } from 'reduxState/store/payment/api';
import { RestrictedUserRoles } from 'reduxState/store/user/constants';
import { selectUserRestrictedRole } from 'reduxState/store/user/selectors';

const Payment: FC = () => {
  const [page, setPage] = useState(1);
  const [isLastPage, setIsLastPage] = useState(false);
  const [loadedPayments, setLoadedPayments] = useState<IPayment[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const params = useParams();
  const navigate = useNavigate();
  const adminAppId = params.adminAppId ?? '';
  const [getPaymentHistory, { data: paymentHistory, isFetching, isError }] = useLazyGetPaymentHistoryQuery();

  const { data: applicationsData, isFetching: isApplicationDataFetching } = useGetUserApplicationsQuery();
  const allAppGroups: AppGroup[] = applicationsData?.appGroups || [];
  const appGroup =
    allAppGroups.find((appGroup: AppGroup) => appGroup.adminId.toString() === adminAppId) || ({} as AppGroup);
  const restrictedRole = useAppSelector(selectUserRestrictedRole);
  const isOperations = restrictedRole === RestrictedUserRoles.OPERATIONS;

  const handleGetPayments = async () => {
    if (!adminAppId) return;
    try {
      const payments = await getPaymentHistory({
        appId: adminAppId,
        page,
      }).unwrap();
      setLoadedPayments(payments);
      setIsInitialLoad(false);
    } catch (error) {
      console.error('Failed to retrieve payment data', error);
      toast.error('Failed to retrieve payment data.');
    }
  };

  const handleLoadMorePayments = async (): Promise<void> => {
    try {
      const newPayments = await getPaymentHistory({ appId: adminAppId, page: page + 1 }).unwrap();
      if (newPayments.length === 0) {
        setIsLastPage(true);
      } else {
        setLoadedPayments(prevPayments => [...prevPayments, ...newPayments]);
        setPage(page + 1);
      }
    } catch (error) {
      console.error('Failed to load more payments.', error);
      toast.error('Failed to load more payments.');
    }
  };

  useEffect(() => {
    handleGetPayments();
    setPage(1);
    setIsLastPage(false);
    setIsInitialLoad(true);
    // Adding handleGetPayments to dependency array can cause infinite loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminAppId]);

  if (isOperations) {
    navigate('/');
    return <></>;
  }

  if (isError) {
    return <Error />;
  }

  if (isInitialLoad && (isFetching || isApplicationDataFetching || !paymentHistory)) {
    return <Loading />;
  }

  const renderBody = () => {
    const Row = ({ index, style, data }: any) => {
      const { paymentHistory, appGroup } = data;
      const payment = paymentHistory[index];
      const displayDate = format(new Date(payment.CreatedDate), 'MMM dd, yyyy');

      return (
        <TableRow style={style} className="flex w-full">
          <TableCell className="text-center font-montserrat w-1/2 flex-1">{displayDate}</TableCell>
          <TableCell className="text-center w-1/2 flex-1">
            <PaymentReportDownload appGroup={appGroup} key={payment.PaymentID} payment={payment} />
          </TableCell>
        </TableRow>
      );
    };

    return (
      <TableBody className="table-body commission-history-body thin-scrollbar">
        <AutoSizer className="!h-[80vh]">
          {({ height, width }: { height: number; width: number }) => (
            <List
              height={height}
              width={width}
              itemCount={loadedPayments.length}
              itemSize={50}
              itemData={{
                paymentHistory: loadedPayments,
                appGroup,
              }}
              onItemsRendered={({ visibleStopIndex }) => {
                if (visibleStopIndex === loadedPayments.length - 1 && !isLastPage) {
                  handleLoadMorePayments();
                }
              }}
              className="thin-scrollbar"
            >
              {Row}
            </List>
          )}
        </AutoSizer>
      </TableBody>
    );
  };

  return (
    <div className="flex justify-center flex-wrap">
      <div className="payment-header max-w-[920px] w-full flex items-center mb-[30px]">
        <h1 className="text-muted-dark-purple">Payment Detail Reports</h1>
      </div>
      <div className="w-full">
        {loadedPayments.length ? (
          <Table className="bg-white max-w-[920px] mx-auto">
            <TableHead
              className="sticky top-0 z-10 table-header-group bg-light-grey"
              sx={{
                '& .MuiTableCell-root': {
                  fontFamily: 'Montserrat, sans-serif',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: 'var(--dark-grey)',
                  textWrap: 'nowrap',
                  textTransform: 'uppercase',
                  padding: '10px',
                  borderRight: '1px solid var(--dark-grey)',
                  backgroundColor: 'var(--light-grey)',

                  '&:last-of-type': {
                    border: 'none',
                  },
                },
              }}
            >
              <TableRow>
                <TableCell className="text-center">Created Date</TableCell>
                <TableCell className="text-center">Payment Details</TableCell>
              </TableRow>
            </TableHead>
            {renderBody()}
          </Table>
        ) : (
          'No payments to display'
        )}
      </div>
    </div>
  );
};

export default Payment;
